import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';


export const useValidationMessages = () => {
    const { t } = useTranslation();

    const setValidationMessage = useCallback((input: HTMLInputElement) => {
        if (input.validity.valueMissing) {
            if (input.type === 'checkbox') {
                input.setCustomValidity(t('validation.checkboxRequired'));
            } else {
                input.setCustomValidity(t('validation.fieldRequired'));
            }
        } else if (input.validity.typeMismatch && input.type === 'email') {
            const value = input.value;
            if (!value.includes('@')) {
                input.setCustomValidity(t('validation.emailMissingAt', { value }));
            } else if (!value.split('@')[1]) {
                input.setCustomValidity(t('validation.emailIncomplete', { value }));
            } else {
                input.setCustomValidity(t('validation.invalidFormat'));
            }
        } else {
            input.setCustomValidity(t('validation.invalidFormat'));
        }
    }, [t]);

    const onInvalid = useCallback((e: FormEvent<HTMLInputElement>) => {
        setValidationMessage(e.currentTarget);
    }, [setValidationMessage]);

    const onInput = useCallback((e: FormEvent<HTMLInputElement>) => {
        const input = e.currentTarget;

        // Clear the custom validity first
        input.setCustomValidity('');

        // Then immediately re-validate to update the message
        if (!input.validity.valid) {
            setValidationMessage(input);
        }
    }, [setValidationMessage]);

    return {
        onInvalid,
        onInput
    };
};

export const useMediaFileThumbnail = (file: File): string => {
    const [ thumbnail, setThumbnail ] = useState('');

    useEffect(() => {
        if (file) {
            if (file.type.startsWith('image/')) {
                getImageThumbnail(file);
            } else if (file.type === 'video/mp4') {
                getMp4VideoThumbnail(file);
            } else {
                setThumbnail('');
            }
        } else {
            setThumbnail('');
        }
    }, [ file ]);


    function getImageThumbnail(imageFile: File): void {
        const reader = new FileReader();

        reader.addEventListener('load', (e) => {
            if (e.target) {
                setThumbnail(e.target.result as string);
            }
        });

        // read the image file as a data URL.
        reader.readAsDataURL(imageFile);
    }

    function getMp4VideoThumbnail(videoFile: File): void {
        const videoUrl = URL.createObjectURL(videoFile);
        const videoEl = document.createElement('video');
        const canvasEl = document.createElement('canvas');
        const canvasCtx = canvasEl.getContext('2d');

        videoEl.muted = true;
        videoEl.controls = false;
        videoEl.volume = 0;
        videoEl.preload = 'metadata';
        videoEl.setAttribute('src', videoUrl);
        videoEl.load();
        videoEl.addEventListener('loadedmetadata', metaDataHandler);

        // Setup canvas for thumbnail capture
        function metaDataHandler() {
            // Set canvas dimensions same as video dimensions
            canvasEl.width = videoEl.videoWidth;
            canvasEl.height = videoEl.videoHeight;

            videoEl.currentTime = 1;
            videoEl.addEventListener('timeupdate', timeUpdateHandler);
        }

        // Draw thumbnail and set thumbnail state
        function timeUpdateHandler() {
            if (canvasCtx && videoEl.videoWidth && videoEl.videoHeight) {
                canvasCtx.drawImage(videoEl, 0, 0, videoEl.videoWidth, videoEl.videoHeight);
                setThumbnail(canvasEl.toDataURL('image/png'));

                videoEl.removeEventListener('loadedmetadata', metaDataHandler);
                videoEl.removeEventListener('timeupdate', timeUpdateHandler);
            }
            URL.revokeObjectURL(videoUrl);
        }
    }

    return thumbnail;
};
